<template>
  <div id="agentes" class="agents-commissions">
    <!-- Programa 1 hero -->
    <section class="agents-hero">
      <div class="container text-center">
        <p class="program-tag">Programa 1 · Membresías</p>
        <h2>Programa de compensación por ventas del plan médico</h2>
        <p class="lede">Somos los que mejor pagamos en el mercado por este tipo de plan.</p>
      </div>
    </section>

    <section class="commissions-body">
      <div class="container">
        <div class="section-header text-center mb-4">
          <h2 class="section-title">Te pagamos cada mes que tu cliente siga activo</h2>
          <p class="section-subtitle">
            Comisión por cada miembro activo, cada mes que paga, sin fecha de fin.
            Se paga el mes siguiente, al cerrar los 30 días de garantía.
          </p>
        </div>

        <div class="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Plan</th>
                <th>Pago mensual al agente</th>
                <th>Otros planes del mercado pagan</th>
                <th>Agencia al mes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in commissionRows" :key="i">
                <td>{{ row[0] }}</td>
                <td class="us">{{ row[1] }}</td>
                <td class="them">{{ row[2] }}</td>
                <td>{{ row[3] }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="know">
          <div>
            <h3>Nivel Élite</h3>
            <p>
              +$2 por miembro desde 250 miembros activos (otros piden 500) y +$3 por familia desde 50 familias.
            </p>
          </div>
          <div>
            <h3>Pago seguro</h3>
            <p>Se paga al cerrar los 30 días de garantía y se descuenta si el cliente pide reembolso.</p>
          </div>
        </div>

        <!-- Simulador 24 meses -->
        <div id="sim" class="calc">
          <div>
            <label for="agents-n-new">Clientes nuevos al mes</label>
            <div class="val">{{ nNew }}</div>
            <input
              id="agents-n-new"
              v-model.number="nNew"
              type="range"
              min="5"
              max="100"
              step="1"
            />
            <div class="annual-toggle">
              <button
                type="button"
                class="switch"
                role="switch"
                :aria-checked="churn"
                aria-label="Incluir cancelaciones"
                @click="churn = !churn"
              >
                <span class="knob" />
              </button>
              <span>Incluir cancelaciones</span>
            </div>
            <p class="note">
              Mezcla de ejemplo: 40% Tu Salud, 20% Senior, 20% Plus y 20% familias de 4.
              Con cancelaciones: 80% llega al mes 3 y luego 5% de bajas al mes.
              Ejemplo ilustrativo, no garantía de ingresos. No incluye nivel Élite.
            </p>
          </div>

          <div>
            <div class="results">
              <div>
                <span>Al mes 12</span>
                <b>{{ fmt(usSeries[11]) }}</b>
              </div>
              <div>
                <span>Al mes 24</span>
                <b>{{ fmt(usSeries[23]) }}</b>
              </div>
              <div>
                <span>Total en 24 meses</span>
                <b>{{ fmt(totalUs) }}</b>
              </div>
            </div>

            <div
              ref="chart"
              class="chart"
              @pointermove="onChartMove"
              @pointerleave="tipVisible = false"
            >
              <svg
                viewBox="0 0 640 280"
                role="img"
                aria-label="Ingreso mensual proyectado del agente en 24 meses"
              >
                <defs>
                  <linearGradient id="agents-ga" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stop-color="#0A6E7D" stop-opacity=".28" />
                    <stop offset="1" stop-color="#0A6E7D" stop-opacity="0" />
                  </linearGradient>
                </defs>
                <line x1="8" x2="632" y1="252" y2="252" stroke="currentColor" opacity=".3" />
                <path :d="usAreaPath" fill="url(#agents-ga)" />
                <path
                  :d="linePath(themSeries)"
                  fill="none"
                  stroke="#9AAFBD"
                  stroke-width="2.5"
                  stroke-dasharray="6 6"
                />
                <path
                  :d="linePath(usSeries)"
                  fill="none"
                  stroke="#0A6E7D"
                  stroke-width="3.5"
                  stroke-linejoin="round"
                />
                <circle
                  v-if="tipVisible"
                  :cx="xAt(tipIndex)"
                  :cy="yAt(usSeries[tipIndex])"
                  r="6"
                  fill="#0A6E7D"
                  stroke="#fff"
                  stroke-width="2"
                />
                <text
                  v-for="m in tickMonths"
                  :key="m"
                  :x="xAt(m - 1)"
                  y="272"
                  :text-anchor="m === 1 ? 'start' : m === 24 ? 'end' : 'middle'"
                  font-size="12"
                  fill="currentColor"
                  opacity=".6"
                >
                  Mes {{ m }}
                </text>
              </svg>
              <div
                class="tip"
                :style="tipStyle"
                :class="{ show: tipVisible }"
                v-html="tipHtml"
              />
            </div>
            <div class="legend">
              <span><i class="leg-us" />Hispano Medical Plan</span>
              <span><i class="leg-them" />Otros planes del mercado</span>
            </div>
          </div>
        </div>

        <!-- Programa 2 · PatientFlow -->
        <div class="pf-band">
          <div class="pf-band-copy">
            <p class="program-tag dark">Programa 2 · aparte</p>
            <h3>PatientFlow: gana por citas, no por membresías</h3>
            <p>
              Es un programa distinto al de comisiones por plan médico. Asigna pacientes a clínicas
              y gana $25 por cita completada.
            </p>
          </div>
          <router-link to="/patientflow" class="pf-link">
            <img :src="pfLogo" alt="PatientFlow" />
            <span>Ver programa PatientFlow</span>
            <i class="fas fa-arrow-right" />
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
/**
 * Comisiones Programa 1 (membresías) + simulador 24 meses + enlace a PatientFlow (Programa 2).
 * Lógica alineada al mockup HMP 2027 (295 HMP / 260 mercado por grupo de 25).
 */
export default {
  name: 'AgentsCommissionsSection',
  data() {
    return {
      nNew: 25,
      churn: false,
      tipVisible: false,
      tipIndex: 0,
      tipHtml: '',
      tipStyle: {},
      tickMonths: [1, 6, 12, 18, 24],
      commissionRows: [
        ['Tu Salud · $55', '$9 por miembro', '$8', '$1'],
        ['Tu Salud Senior · $64', '$8 por miembro', '$6', '$1'],
        ['Tu Salud Plus · $89', '$11 por miembro', '$10', '$1'],
        [
          'Familiar · $95 a $175',
          '$12 a $27 por familia ($22 en familia de 4)',
          '$20 (familia de 4)',
          '$2'
        ]
      ],
      pfLogo: require('@/assets/lp/patientflow/patientflow-logo.png'),
      // mockup: comisión por grupo de 25 clientes
      usPerGroup: 295,
      themPerGroup: 260,
      chart: { W: 640, H: 280, pl: 8, pr: 8, pt: 16, pb: 28 }
    }
  },
  computed: {
    usSeries() {
      return this.series(this.usPerGroup, this.nNew)
    },
    themSeries() {
      return this.series(this.themPerGroup, this.nNew)
    },
    totalUs() {
      return this.usSeries.reduce((a, b) => a + b, 0)
    },
    chartMax() {
      const m = Math.max(...this.usSeries, 1)
      return m * 1.08
    },
    usAreaPath() {
      const { H, pb } = this.chart
      return `${this.linePath(this.usSeries)}L${this.xAt(23)},${H - pb}L${this.xAt(0)},${H - pb}Z`
    }
  },
  methods: {
    fmt(n) {
      return `$${Math.round(n || 0).toLocaleString('en-US')}`
    },
    surv(a) {
      if (a < 1) return 1
      if (a < 3) return [1, 0.9, 0.85][a - 1]
      return 0.8 * Math.pow(0.95, a - 3)
    },
    series(perGroup, n) {
      const g = (perGroup * n) / 25
      const out = []
      for (let M = 1; M <= 24; M += 1) {
        let t = 0
        for (let c = 1; c < M; c += 1) {
          const age = M - c
          t += g * (this.churn ? this.surv(age) : 1)
        }
        out.push(t)
      }
      return out
    },
    xAt(i) {
      const { W, pl, pr } = this.chart
      return pl + (i * (W - pl - pr)) / 23
    },
    yAt(v) {
      const { H, pt, pb } = this.chart
      return H - pb - (v / this.chartMax) * (H - pt - pb)
    },
    linePath(s) {
      return s
        .map((v, i) => `${i ? 'L' : 'M'}${this.xAt(i).toFixed(1)},${this.yAt(v).toFixed(1)}`)
        .join('')
    },
    onChartMove(ev) {
      const el = this.$refs.chart
      if (!el) return
      const svg = el.querySelector('svg')
      if (!svg) return
      const r = svg.getBoundingClientRect()
      const { W, pl, pr } = this.chart
      const px = ((ev.clientX - r.left) / r.width) * W
      let i = Math.round((px - pl) / ((W - pl - pr) / 23))
      i = Math.max(0, Math.min(23, i))
      this.tipIndex = i
      this.tipVisible = true
      this.tipHtml = `Mes ${i + 1}: <b>${this.fmt(this.usSeries[i])}</b> · otros ${this.fmt(
        this.themSeries[i]
      )}`
      this.tipStyle = {
        left: `${(this.xAt(i) / W) * 100}%`,
        top: `${(this.yAt(this.usSeries[i]) / this.chart.H) * r.height}px`
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../styles/variables';

.agents-hero {
  background: linear-gradient(135deg, #0d2b45 0%, #0b4a6b 55%, #08707f 100%);
  color: #f3f7f8;
  text-align: center;
  padding: 72px 0;
}

.program-tag {
  display: inline-block;
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $landing-secondary-color;
  margin-bottom: 12px;

  &.dark {
    color: $landing-teal;
  }
}

.agents-hero h2 {
  max-width: 900px;
  margin: 0 auto 16px;
  font-size: clamp(1.6rem, 3.5vw, 2.4rem);
  font-weight: 800;
}

.agents-hero .lede {
  color: #cfe2ea;
  font-size: 1.2rem;
  margin: 0;
}

.commissions-body {
  padding: 72px 0 80px;
  background: $landing-bg-white;
}

.section-title {
  font-size: clamp(1.6rem, 3vw, 2.25rem);
  font-weight: 800;
  color: $landing-primary-color;
  margin-bottom: 0.5rem;
}

.section-subtitle {
  color: $landing-text-medium;
  font-size: 1.05rem;
  max-width: 70ch;
  margin: 0 auto;
}

.tbl-wrap {
  overflow-x: auto;
  border: 1px solid $landing-border-light;
  border-radius: 18px;
  background: #fff;
  margin-top: 28px;
}

table {
  width: 100%;
  border-collapse: collapse;
  min-width: 640px;
}

th,
td {
  padding: 14px 18px;
  text-align: left;
  border-bottom: 1px solid $landing-border-light;
  font-size: 0.98rem;
}

th {
  background: $landing-bg-light;
  color: $landing-primary-color;
  font-weight: 700;
}

tbody tr:last-child td {
  border-bottom: 0;
}

.us {
  color: $landing-teal;
  font-weight: 700;
}

.them {
  color: $landing-text-medium;
}

.know {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-top: 28px;
}

.know > div {
  border-left: 4px solid $landing-teal;
  padding: 6px 0 6px 20px;
}

.know h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: $landing-primary-color;
}

.know p {
  color: $landing-text-medium;
  margin: 6px 0 0;
  line-height: 1.45;
}

.calc {
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 32px;
  background: #fff;
  border: 1px solid $landing-border-light;
  border-radius: 24px;
  padding: 32px;
  margin-top: 40px;
}

.calc label {
  display: block;
  font-weight: 700;
  margin-bottom: 6px;
  color: $landing-primary-color;
}

.calc .val {
  font-weight: 850;
  font-size: 2.5rem;
  line-height: 1;
  color: $landing-accent-text;
}

input[type='range'] {
  width: 100%;
  accent-color: $landing-teal;
  margin: 12px 0 22px;
}

.annual-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
  font-size: 0.95rem;
  color: $landing-text-medium;
}

.switch {
  width: 46px;
  height: 26px;
  border-radius: 999px;
  border: 0;
  background: #c5d4da;
  position: relative;
  cursor: pointer;
  padding: 0;

  &[aria-checked='true'] {
    background: $landing-teal;
  }

  .knob {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    transition: transform 0.2s ease;
  }

  &[aria-checked='true'] .knob {
    transform: translateX(20px);
  }
}

.note {
  font-size: 0.85rem;
  color: $landing-text-medium;
  margin: 0;
  line-height: 1.45;
}

.results {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 18px;
}

.results div {
  border-top: 2px solid $landing-primary-color;
  padding-top: 10px;
}

.results span {
  font-size: 0.9rem;
  color: $landing-text-medium;
  display: block;
}

.results b {
  font-size: 1.75rem;
  font-weight: 850;
  color: $landing-primary-color;
}

.chart {
  position: relative;
  color: $landing-text-medium;
}

.chart svg {
  width: 100%;
  height: auto;
  display: block;
}

.tip {
  position: absolute;
  pointer-events: none;
  background: $landing-primary-color;
  color: #fff;
  font-size: 0.85rem;
  padding: 8px 10px;
  border-radius: 8px;
  white-space: nowrap;
  transform: translate(-50%, -110%);
  opacity: 0;
  transition: opacity 0.15s;

  &.show {
    opacity: 1;
  }
}

.legend {
  display: flex;
  gap: 18px;
  font-size: 0.9rem;
  color: $landing-text-medium;
  margin-top: 8px;
  flex-wrap: wrap;
}

.legend i {
  display: inline-block;
  width: 14px;
  height: 4px;
  border-radius: 2px;
  vertical-align: middle;
  margin-right: 6px;
}

.leg-us {
  background: $landing-teal;
}

.leg-them {
  background: #9aafbd;
}

.pf-band {
  margin-top: 48px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  background: $landing-bg-light;
  border: 1px solid $landing-border-light;
  border-radius: 20px;
  padding: 28px 32px;
}

.pf-band-copy {
  max-width: 52ch;

  h3 {
    margin: 0 0 8px;
    font-size: 1.35rem;
    font-weight: 800;
    color: $landing-primary-color;
  }

  p {
    margin: 0;
    color: $landing-text-medium;
    line-height: 1.45;
  }
}

.pf-link {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  color: #0b2f63;
  border-radius: 12px;
  padding: 12px 18px;
  text-decoration: none;
  font-weight: 700;
  border: 1px solid $landing-border-light;
  box-shadow: 0 8px 24px $landing-primary-rgba-08;

  img {
    height: 30px;
    width: auto;
  }

  &:hover {
    text-decoration: none;
    color: $landing-primary-color;
    border-color: $landing-secondary-color;
  }
}

@media (max-width: 900px) {
  .calc {
    grid-template-columns: 1fr;
    padding: 24px;
  }

  .results {
    grid-template-columns: 1fr 1fr;
  }

  .know {
    grid-template-columns: 1fr;
  }

  .agents-hero,
  .commissions-body {
    padding: 48px 0;
  }

  .pf-band {
    padding: 22px;
  }

  .pf-link {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 560px) {
  .results {
    grid-template-columns: 1fr;
  }

  .results b {
    font-size: 1.45rem;
  }

  .tbl-wrap {
    border-radius: 12px;
  }
}
</style>
